import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { resourceService } from '../../../services/resourceService';

const RESOURCE_COLUMNS = [
  'name', 'category', 'status', 'description', 'hourly_rate', 'daily_rate'
];

const CATEGORIES = [
  'employees', 'contractors', 'teams', 'trucks', 'trailers', 'equipment', 'tools', 'other'
];

const STATUSES = ['Available', 'In Use', 'Maintenance', 'Inactive'];

const parseCSV = (text) => {
  const lines = text?.trim()?.split('\n');
  if (lines?.length < 2) return { headers: [], rows: [] };
  const headers = lines?.[0]?.split(',')?.map(h => h?.trim()?.replace(/^"|"$/g, ''));
  const rows = lines?.slice(1)?.map(line => {
    const values = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line?.length; i++) {
      if (line?.[i] === '"') {
        inQuotes = !inQuotes;
      } else if (line?.[i] === ',' && !inQuotes) {
        values?.push(current?.trim());
        current = '';
      } else {
        current += line?.[i];
      }
    }
    values?.push(current?.trim());
    return headers?.reduce((obj, h, i) => {
      obj[h] = values?.[i] ?? '';
      return obj;
    }, {});
  });
  return { headers, rows };
};

const toCSV = (resources) => {
  const headers = ['id', 'name', 'category', 'status', 'description', 'hourly_rate', 'daily_rate', 'created_at'];
  const escape = (val) => {
    if (val === null || val === undefined) return '';
    const str = String(val);
    if (str?.includes(',') || str?.includes('"') || str?.includes('\n')) {
      return `"${str?.replace(/"/g, '""')}"`;
    }
    return str;
  };
  const rows = resources?.map(r =>
    headers?.map(h => escape(r?.[h] ?? r?.[h?.replace(/_([a-z])/g, (_, c) => c?.toUpperCase())]))?.join(',')
  );
  return [headers?.join(','), ...rows]?.join('\n');
};

const ResourceImportExportTab = () => {
  const fileInputRef = useRef(null);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importErrors, setImportErrors] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);

  const handleFileSelect = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    if (!file?.name?.endsWith('.csv')) {
      setImportErrors(['Please select a valid CSV file.']);
      setImportPreview(null);
      setImportFile(null);
      return;
    }
    setImportFile(file);
    setImportResult(null);
    setImportErrors([]);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev?.target?.result;
      const { headers, rows } = parseCSV(text);
      const errors = [];
      if (!headers?.includes('name')) errors?.push('CSV must have a "name" column.');
      if (!headers?.includes('category')) errors?.push('CSV must have a "category" column.');
      const validRows = rows?.filter(r => r?.name?.trim());
      if (validRows?.length === 0) errors?.push('No valid rows found in CSV.');
      setImportErrors(errors);
      setImportPreview({ headers, rows: validRows?.slice(0, 5), total: validRows?.length, allRows: validRows });
    };
    reader?.readAsText(file);
  };

  const handleImport = async () => {
    if (!importPreview || importErrors?.length > 0) return;
    setImporting(true);
    setImportResult(null);
    let successCount = 0;
    let failCount = 0;
    const rowErrors = [];
    const toTitleCase = (str) =>
      str?.trim()?.replace(/\w\S*/g, (word) => word?.charAt(0)?.toUpperCase() + word?.slice(1)?.toLowerCase());
    for (const row of importPreview?.allRows) {
      const category = CATEGORIES?.includes(row?.category?.toLowerCase())
        ? row?.category?.toLowerCase()
        : 'other';
      const status = STATUSES?.includes(row?.status) ? row?.status : 'Available';
      const resourceData = {
        name: toTitleCase(row?.name),
        category,
        status,
        description: row?.description?.trim() || null,
        hourlyRate: row?.hourly_rate ? parseFloat(row?.hourly_rate) || null : null,
        dailyRate: row?.daily_rate ? parseFloat(row?.daily_rate) || null : null,
      };
      const { error } = await resourceService?.create(resourceData);
      if (error) {
        failCount++;
        rowErrors?.push(`Row "${row?.name}": ${error?.message}`);
      } else {
        successCount++;
      }
    }
    setImportResult({ successCount, failCount, rowErrors });
    setImporting(false);
    if (successCount > 0) {
      setImportFile(null);
      setImportPreview(null);
      if (fileInputRef?.current) fileInputRef.current.value = '';
    }
  };

  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      const { data, error } = await resourceService?.getAll();
      if (error) throw error;
      const flat = (data || [])?.map(r => ({
        id: r?.id,
        name: r?.name,
        category: r?.category,
        status: r?.status,
        description: r?.description,
        hourly_rate: r?.hourlyRate,
        daily_rate: r?.dailyRate,
        created_at: r?.createdAt,
      }));
      const csv = toCSV(flat);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `resources_export_${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
      document.body?.appendChild(a);
      a?.click();
      document.body?.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(err?.message || 'Failed to export resources.');
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadTemplate = () => {
    const template = RESOURCE_COLUMNS?.join(',') + '\nExample Employee,employees,Available,Senior field technician,45.00,320.00';
    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'resources_import_template.csv';
    document.body?.appendChild(a);
    a?.click();
    document.body?.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    const file = e?.dataTransfer?.files?.[0];
    if (file) {
      const fakeEvent = { target: { files: [file] } };
      handleFileSelect(fakeEvent);
    }
  };

  return (
    <div className="space-y-8">
      {/* Export Section */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
            <Icon name="Download" size={20} className="text-green-500" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-foreground">Export Resources</h3>
            <p className="text-sm text-muted-foreground">Download all resources as a CSV file</p>
          </div>
        </div>
        {exportError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
            <Icon name="AlertCircle" size={16} />
            {exportError}
          </div>
        )}
        <div className="flex flex-wrap gap-3">
          <Button
            variant="default"
            iconName="Download"
            iconPosition="left"
            onClick={handleExport}
            disabled={exporting}
          >
            {exporting ? 'Exporting...' : 'Export All Resources'}
          </Button>
          <Button
            variant="outline"
            iconName="FileText"
            iconPosition="left"
            onClick={handleDownloadTemplate}
          >
            Download Import Template
          </Button>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Exported columns: id, name, category, status, description, hourly_rate, daily_rate, created_at
        </p>
      </div>
      {/* Import Section */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
            <Icon name="Upload" size={20} className="text-accent" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-foreground">Import Resources</h3>
            <p className="text-sm text-muted-foreground">Bulk import resources from a CSV file</p>
          </div>
        </div>

        {/* Drop Zone */}
        <div
          className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${
            importFile ? 'border-accent bg-accent/5' : 'border-border hover:border-accent/50 hover:bg-muted/30'
          }`}
          onDrop={handleDrop}
          onDragOver={(e) => e?.preventDefault()}
          onClick={() => fileInputRef?.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={handleFileSelect}
          />
          <Icon name={importFile ? 'FileCheck' : 'UploadCloud'} size={36} className={`mx-auto mb-3 ${importFile ? 'text-accent' : 'text-muted-foreground'}`} />
          {importFile ? (
            <>
              <p className="font-medium text-foreground">{importFile?.name}</p>
              <p className="text-sm text-muted-foreground mt-1">
                {importPreview ? `${importPreview?.total} row${importPreview?.total !== 1 ? 's' : ''} detected` : 'Reading file...'}
              </p>
            </>
          ) : (
            <>
              <p className="font-medium text-foreground">Drop your CSV file here</p>
              <p className="text-sm text-muted-foreground mt-1">or click to browse — .csv files only</p>
            </>
          )}
        </div>

        {/* Errors */}
        {importErrors?.length > 0 && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2 text-red-700 font-medium text-sm mb-2">
              <Icon name="AlertCircle" size={16} />
              Validation Errors
            </div>
            <ul className="list-disc list-inside space-y-1">
              {importErrors?.map((e, i) => (
                <li key={i} className="text-sm text-red-600">{e}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Preview Table */}
        {importPreview && importErrors?.length === 0 && (
          <div className="mt-4">
            <p className="text-sm font-medium text-foreground mb-2">
              Preview (first {Math.min(5, importPreview?.rows?.length)} of {importPreview?.total} rows)
            </p>
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    {importPreview?.headers?.map(h => (
                      <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {importPreview?.rows?.map((row, i) => (
                    <tr key={i} className="hover:bg-muted/30">
                      {importPreview?.headers?.map(h => (
                        <td key={h} className="px-3 py-2 text-foreground truncate max-w-[160px]">{row?.[h] || <span className="text-muted-foreground">—</span>}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Import Result */}
        {importResult && (
          <div className={`mt-4 p-4 rounded-lg border ${
            importResult?.failCount === 0
              ? 'bg-green-50 border-green-200'
              : importResult?.successCount === 0
              ? 'bg-red-50 border-red-200' :'bg-yellow-50 border-yellow-200'
          }`}>
            <div className="flex items-center gap-2 font-medium text-sm mb-1">
              <Icon
                name={importResult?.failCount === 0 ? 'CheckCircle' : 'AlertTriangle'}
                size={16}
                className={importResult?.failCount === 0 ? 'text-green-600' : 'text-yellow-600'}
              />
              <span className={importResult?.failCount === 0 ? 'text-green-700' : 'text-yellow-700'}>
                Import Complete
              </span>
            </div>
            <p className="text-sm text-muted-foreground">
              {importResult?.successCount} resource{importResult?.successCount !== 1 ? 's' : ''} imported successfully
              {importResult?.failCount > 0 && `, ${importResult?.failCount} failed`}.
            </p>
            {importResult?.rowErrors?.length > 0 && (
              <ul className="mt-2 list-disc list-inside space-y-1">
                {importResult?.rowErrors?.slice(0, 5)?.map((e, i) => (
                  <li key={i} className="text-xs text-red-600">{e}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Import Button */}
        {importPreview && importErrors?.length === 0 && !importResult && (
          <div className="mt-4 flex gap-3">
            <Button
              variant="default"
              iconName="Upload"
              iconPosition="left"
              onClick={handleImport}
              disabled={importing}
            >
              {importing ? `Importing ${importPreview?.total} resources...` : `Import ${importPreview?.total} Resource${importPreview?.total !== 1 ? 's' : ''}`}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setImportFile(null);
                setImportPreview(null);
                setImportErrors([]);
                if (fileInputRef?.current) fileInputRef.current.value = '';
              }}
            >
              Cancel
            </Button>
          </div>
        )}

        {/* Column Reference */}
        <div className="mt-6 p-4 bg-muted/40 rounded-lg">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Required & Optional CSV Columns</p>
          <div className="flex flex-wrap gap-2">
            {[{ col: 'name', required: true }, { col: 'category', required: true }, { col: 'status', required: false }, { col: 'description', required: false }, { col: 'hourly_rate', required: false }, { col: 'daily_rate', required: false }]?.map(({ col, required }) => (
              <span key={col} className={`px-2 py-1 rounded text-xs font-mono ${
                required ? 'bg-accent/10 text-accent border border-accent/20' : 'bg-muted text-muted-foreground border border-border'
              }`}>
                {col}{required ? ' *' : ''}
              </span>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Valid categories: {CATEGORIES?.join(', ')} &nbsp;|&nbsp; Valid statuses: {STATUSES?.join(', ')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResourceImportExportTab;
